import React, { useState } from "react";
import clsx from "clsx";
import {
    makeStyles,
    Grid,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Link,
} from "@material-ui/core";

import PhoneIcon from "@material-ui/icons/Phone";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";
import EmailIcon from "@material-ui/icons/Email";
import StorefrontIcon from "@material-ui/icons/Storefront";

import NoticeDialog from "../NoticeDialog";
import TermsOfUse from "../TermsOfUse";
import useTranslation from "../../application/translation";
import logo from "../../wtw_new_logo_white.svg";

const useStyles = makeStyles((theme) => ({
    root: {
        "& > a": {
            marginBottom: ".5rem",
            display: "block",
            color: "#fff",
            fontSize: 12,
            cursor: "pointer",
        },
    },
    secondaryLinks: {
        "& > a": {
            color: theme.palette.text.hint,
        },
    },
    dialog: {
        "& * > svg": {
            marginBottom: "-3px",
            marginRight: ".2rem",
        },
        "& > h6": {
            marginTop: "1rem",
        },
    },
    appBar: {
        background: "#7F35B2",
        color: "#fff",
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(1),
    },
    logo: {
        maxWidth: "360px",
        height: "50px",
        marginLeft: "-10px",
        verticalAlign: "middle",
        borderStyle: "none",
        marginBottom: ".5rem",
        [theme.breakpoints.only("xs")]: {
            maxWidth: "340px",
            marginBottom: "0",
        },
    },
    links: {
        color: "#d8d8d8",
        border: 0,
        [theme.breakpoints.only("xs")]: {
            marginTop: ".5rem",
            paddingTop: ".5rem",
            borderTop: "1px solid #2a2a2a",
        },
    },
    copyright: {
        marginTop: 15,
        paddingTop: 15,
        paddingBottom: 10,
        borderTop: "1px solid white",
        fontSize: 12,
    },
}));

const Footer = () => {
    const styles = useStyles();
    const [open, toggleOpen] = useState(false);
    const [showCookie, setShowCookie] = useState(false);
    const [showTou, setShowTou] = useState(false);
    const translation = useTranslation();

    const contactUs = {
        title: translation.footer_contactus,
        content: (
            <>
                <Typography>
                    <Link
                        href={`tel:${translation.footer_contact_tel}`}
                        aria-label={`Ring till ${translation.footer_contact_tel}`}
                    >
                        <PhoneIcon color="primary" /> {translation.footer_contact_tel}
                    </Link>
                    <br />
                    <Link
                        href={`mailto:${translation.footer_contact_email}`}
                        aria-label={`Skicka e-post till ${translation.footer_contact_email}`}
                    >
                        <AlternateEmailIcon color="primary" /> {translation.footer_contact_email}
                    </Link>
                </Typography>
                <Typography variant="h6">
                    <StorefrontIcon color="primary" /> {translation.footer_contact1}
                </Typography>
                <Typography>{translation.footer_contact2}</Typography>
                <Typography variant="h6">
                    <EmailIcon color="primary" /> {translation.footer_contact3}
                </Typography>
                <Typography>
                    {translation.footer_contact5}
                    <br /> {translation.footer_contact4}
                    <br /> {translation.footer_contact6}
                    <br /> {translation.footer_contact7}
                </Typography>
            </>
        ),
    };

    const clickHandler = (e) => {
        e.preventDefault();
        toggleOpen(true);
    };

    return (
        <div className={styles.appBar}>
            {showCookie && <NoticeDialog onExit={() => setShowCookie(false)} />}
            {showTou && <TermsOfUse onExit={() => setShowTou(false)} />}

            <Container>
                <Grid container direction="row">
                    <Grid item xs={12} md={6}>
                        <img
                            src={logo}
                            className={styles.logo}
                            alt="Willis Towers Watson Sweden AB logotyp"
                        />
                    </Grid>
                    <Grid
                        container
                        item
                        xs={12}
                        md={6}
                        justifyContent="space-between"
                        className={styles.links}
                    >
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" className={styles.root}>
                                <Link
                                    href="https://www.wtwco.com/sv-se/notices/information-om-oss-som-forsakringsformedlare"
                                    aria-label="Information om oss som försäkringsförmedlare"
                                >
                                    {translation.footer_aboutus}
                                </Link>
                                <Link onClick={clickHandler} aria-label="Kontakta oss">
                                    {translation.footer_contactus}
                                </Link>
                                <Link
                                    href="https://www.wtwco.com/sv-se/notices/klagomal"
                                    aria-label="Klagomål"
                                >
                                    {translation.footer_complaints}
                                </Link>
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" className={clsx(styles.root)}>
                                <Link onClick={() => setShowTou(true)} aria-label="Användarvillkor">
                                    Terms of use
                                </Link>
                                <Link onClick={() => setShowCookie(true)} aria-label="Cookiepolicy">
                                    Cookie notice
                                </Link>
                                <Link
                                    href="https://www.wtwco.com/-/media/wtw/notices/code-of-conduct-english.pdf?modified=20200925121400"
                                    aria-label="Code of conduct PDF"
                                >
                                    Code of conduct
                                </Link>
                                <Link
                                    href="https://www.wtwco.com/sv-se/notices/privacy-notice-crb"
                                    aria-label="Integritetspolicy för WTW Sweden och WTW Oy Ab"
                                >
                                    Privacy notice - Willis Towers Watson Sweden AB
                                    <br /> and Willis Towers Watson Oy Ab
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography className={styles.copyright}>
                            Copyright © {new Date().getFullYear()} WTW. All rights reserved.
                        </Typography>
                    </Grid>
                </Grid>
            </Container>

            <Dialog
                maxWidth="sm"
                fullWidth
                open={open}
                onClose={() => toggleOpen(false)}
                aria-labelledby="dialog-title"
                aria-describedby="dialog-description"
            >
                <DialogTitle id="dialog-title">{contactUs.title}</DialogTitle>
                <DialogContent
                    id="dialog-description"
                    className={styles.dialog}
                >
                    {contactUs.content}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => toggleOpen(false)} color="primary">
                        Stäng
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Footer;
